<div class="page-container">
  <!-- Main Content -->
  <main class="page-main">
    <div class="page-content full-height">
      <!-- Header Section -->
      <div class="page-title-container">
        <app-breadcrumb></app-breadcrumb>
        <h1 class="page-title">Requests Management</h1>
        <div class="page-title-actions">
          <div
            class="inline-action export-action"
            [class.is-disabled]="isExporting"
            role="button"
            [attr.tabindex]="isExporting ? -1 : 0"
            [attr.aria-disabled]="isExporting"
            (click)="openExportConfig()"
            (keydown.enter)="openExportConfig()"
            (keydown.space)="$event.preventDefault(); openExportConfig()"
            matTooltip="Configure and export to Excel"
          >
            @if (isExporting) {
              <mat-spinner diameter="18"></mat-spinner>
            } @else {
              <mat-icon class="inline-action-icon">download</mat-icon>
            }
            <span>Export</span>
          </div>
          @if (canManageRequests) {
            <div class="inline-action" (click)="onNewRequest()">
              <mat-icon class="inline-action-icon">add</mat-icon>
              <span>New Request</span>
            </div>
          }
        </div>
      </div>

      <!-- Filters Section -->
      <mat-card class="filters-card no-padding">
        <mat-card-content>
          <form [formGroup]="filterForm" class="filters-form">
            <div class="filters-container">
              <!-- Main Search Row with Actions -->
              <div class="main-search-row">
                <mat-form-field
                  appearance="outline"
                  class="filter-field search-field"
                >
                  <mat-label
                    >Search requests by request id, requestor name, or description</mat-label
                  >
                  <input
                    matInput
                    [formControl]="searchControl"
                    placeholder="Search requests by request id, requestor name, or description"
                    autocomplete="off"
                  />
                  <mat-icon
                    matSuffix
                    [style.cursor]="searchControl.value ? 'pointer' : 'default'"
                    (click)="searchControl.value ? searchControl.reset() : null"
                    >{{ searchControl.value ? "close" : "search" }}</mat-icon
                  >
                </mat-form-field>

                <!-- Request Type Filter -->
                <mat-form-field
                  appearance="outline"
                  class="filter-field request-type-field"
                >
                  <mat-label>Request Type</mat-label>
                  <mat-select
                    formControlName="requestTypes"
                    multiple
                    (selectionChange)="onFilterChange()"
                  >
                    @for (type of availableRequestTypes; track type.value) {
                      <mat-option [value]="type.value">
                        {{ type.label }}
                      </mat-option>
                    }
                  </mat-select>
                </mat-form-field>

                <!-- Practice Area Filter -->
                <mat-form-field
                  appearance="outline"
                  class="filter-field practice-areas-field"
                >
                  <mat-label
                    >Practice Areas
                    {{
                      getSelectedCountDisplay("practiceAreaCodes")
                    }}</mat-label
                  >
                  <mat-select
                    formControlName="practiceAreaCodes"
                    multiple
                    (selectionChange)="onFilterChange()"
                  >
                    @for (pa of filterPracticeAreas; track pa.practiceCode) {
                      <mat-option [value]="pa.practiceCode">
                        {{ pa.description }}
                      </mat-option>
                    }
                  </mat-select>
                </mat-form-field>

                <!-- Status Filter -->
                <mat-form-field
                  appearance="outline"
                  class="filter-field status-field"
                >
                  <mat-label>Status</mat-label>
                  <mat-select
                    [(value)]="selectedStatuses"
                    multiple
                    (selectionChange)="onStatusFilterChange($event.value)"
                  >
                    <mat-option value="New">New</mat-option>
                    <mat-option value="Pending">Pending</mat-option>
                    <mat-option value="Fulfilled">Fulfilled</mat-option>
                  </mat-select>
                </mat-form-field>

                <!-- Filter Actions -->
                <div class="filter-actions">
                  <button
                    mat-stroked-button
                    class="reset-filters-btn"
                    type="button"
                    (click)="resetFilters()"
                    [disabled]="!hasFiltersChanged()"
                  >
                    <mat-icon>replay</mat-icon>
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </form>
        </mat-card-content>
      </mat-card>

      <!-- Requests Table -->
      <mat-card class="table-card no-padding">
        <div class="requests-table-container">
          <!-- Loading State (for search operations only) -->
          @if (isSearching) {
            <div class="loading-state">
              <mat-spinner diameter="32"></mat-spinner>
            </div>
          }

          <!-- Error State -->
          @if (error && !isLoading) {
            <app-error-state
              [message]="error"
              (retry)="loadRequests()"
            ></app-error-state>
          }

          <!-- Empty State -->
          @if (!isLoading && !error && requests.length === 0) {
            <div class="empty-state">
              <mat-icon class="empty-icon">business</mat-icon>
              <h3>No requests found</h3>
              <p>Try adjusting your search criteria or filters.</p>
            </div>
          }

          <!-- Requests Table - Show during non-search loading (skeleton) or when data exists -->
          @if (!isSearching && !error && (isLoading || requests.length > 0)) {
            <!-- Table Header - Fixed at top -->
            <div class="table-header-scroll" #headerScroll>
            <table
              mat-table
              [dataSource]="[]"
              matSort
              [matSortActive]="currentSortActive"
              [matSortDirection]="currentSortDirection"
              #headerSort="matSort"
              class="requests-table header-only"
              [style.min-width.px]="tableMinWidth"
            >
              <!-- Date Submitted Column Header -->
              <ng-container matColumnDef="dateSubmitted">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>
                  Date Submitted
                </th>
              </ng-container>

              <!-- Request ID Column Header -->
              <ng-container matColumnDef="id">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>Id</th>
              </ng-container>

              <!-- Client Matter Column Header -->
              <ng-container matColumnDef="clientMatter">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>
                  Client Matter
                </th>
              </ng-container>

              <!-- Practice Area Column Header -->
              <ng-container matColumnDef="practiceArea">
                <th mat-header-cell *matHeaderCellDef>Practice Areas</th>
              </ng-container>

              <!-- Description Column Header -->
              <ng-container matColumnDef="description">
                <th mat-header-cell *matHeaderCellDef>Description</th>
              </ng-container>

              <!-- Requestor Name Column Header -->
              <ng-container matColumnDef="requestorName">
                <th mat-header-cell *matHeaderCellDef>Requestor Name</th>
              </ng-container>

              <!-- Request Type Column Header -->
              <ng-container matColumnDef="requestType">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>
                  Request Type
                </th>
              </ng-container>

              <!-- Status Column Header -->
              <ng-container matColumnDef="status">
                <th mat-header-cell *matHeaderCellDef>Status</th>
              </ng-container>

              <!-- Assigned Column Header -->
              <ng-container matColumnDef="assigned">
                <th mat-header-cell *matHeaderCellDef>Assigned</th>
              </ng-container>

              <!-- Actions Column Header -->
              <ng-container matColumnDef="actions">
                <th mat-header-cell *matHeaderCellDef></th>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            </table>
            </div>

            <!-- Table Body - Scrollable -->
            <!-- cdkScrollable registers this container with the CDK ScrollDispatcher so the
                 Assigned popover's scroll strategy sees the table's own scrolling, not just
                 the window's. -->
            <div
              class="table-scroll-wrapper"
              cdkScrollable
              (scroll)="syncHeaderScroll($event, headerScroll)"
            >
              <table
                mat-table
                [dataSource]="requests"
                [trackBy]="trackByRequestId"
                class="requests-table body-only"
                [style.min-width.px]="tableMinWidth"
              >
                <!-- Date Submitted Column -->
                <ng-container matColumnDef="dateSubmitted">
                  <td mat-cell *matCellDef="let request" class="date-cell">
                    @if (request.id < 0) {
                      <div class="skeleton-line medium"></div>
                    } @else {
                      {{ formatDate(request.dateSubmitted) }}
                    }
                  </td>
                </ng-container>

                <!-- Request ID Column -->
                <ng-container matColumnDef="id">
                  <td mat-cell *matCellDef="let request" class="id-cell">
                    @if (request.id < 0) {
                      <div class="skeleton-line short"></div>
                    } @else {
                      {{ request.id }}
                    }
                  </td>
                </ng-container>

                <!-- Client Matter Column -->
                <ng-container matColumnDef="clientMatter">
                  <td mat-cell *matCellDef="let request" class="matter-cell">
                    @if (request.id < 0) {
                      <div class="skeleton-line medium"></div>
                    } @else {
                      @let primaryMatter =
                        request.clientMatterNumbers?.length > 0
                          ? request.clientMatterNumbers[0]
                          : request.clientMatterNumber;
                      @let matterCount =
                        request.clientMatterNumbers?.length ||
                        (request.clientMatterNumber ? 1 : 0);
                      @if (matterCount > 1) {
                        <button
                          type="button"
                          class="matter-multiple-link"
                          (click)="openMattersDialog(request, $event)"
                        >
                          Multiple Matters
                        </button>
                      } @else if (primaryMatter) {
                        <app-matter-cell
                          [matterNumber]="primaryMatter"
                          [matterName]="request.matterTitle"
                          [clientName]="request.clientName"
                          [routerLink]="['/matters', primaryMatter]"
                          [linkState]="{
                            breadcrumbOverride: [
                              { label: 'Requests', url: '/requests' },
                              { label: ':matterId', url: '' },
                            ],
                          }"
                        />
                      } @else {
                        -
                      }
                    }
                  </td>
                </ng-container>

                <!-- Practice Area Column -->
                <ng-container matColumnDef="practiceArea">
                  <td
                    mat-cell
                    *matCellDef="let request"
                    class="practice-area-cell"
                  >
                    @if (request.id < 0) {
                      <div class="skeleton-line short"></div>
                    } @else if (request.practiceAreaCodes?.length > 0) {
                      <app-membership-chips
                        [items]="
                          getPracticeAreaLabels(request.practiceAreaCodes)
                        "
                        labelKey="description"
                      />
                    } @else {
                      -
                    }
                  </td>
                </ng-container>

                <!-- Description Column -->
                <ng-container matColumnDef="description">
                  <td
                    mat-cell
                    *matCellDef="let request"
                    class="description-cell"
                  >
                    @if (request.id < 0) {
                      <div class="skeleton-line long"></div>
                    } @else {
                      <a
                        class="cell-link description-text"
                        #descText
                        [routerLink]="['/requests', request.id]"
                        [matTooltip]="request.description"
                        [matTooltipDisabled]="true"
                        #tooltip="matTooltip"
                        (mouseenter)="updateTooltipDisabled(descText, tooltip)"
                        matTooltipClass="app-tooltip"
                        matTooltipPosition="above"
                      >
                        {{ request.description || "-" }}
                      </a>
                    }
                  </td>
                </ng-container>

                <!-- Requestor Name Column -->
                <ng-container matColumnDef="requestorName">
                  <td mat-cell *matCellDef="let request" class="name-cell">
                    @if (request.id < 0) {
                      <div class="requestor-info skeleton-row">
                        <div class="skeleton-name-lines">
                          <div class="skeleton-line long"></div>
                        </div>
                      </div>
                    } @else {
                      <div class="requestor-info">
                        <div class="requestor-details">
                          @if (
                            request.requestors && request.requestors.length > 0
                          ) {
                            @for (
                              requestor of request.requestors;
                              track requestor.employeeId
                            ) {
                              <div class="requestor-name">
                                <strong>{{
                                  requestor.fullName ||
                                    requestor.firstName +
                                      " " +
                                      requestor.lastName ||
                                    requestor.employeeId
                                }}</strong>
                              </div>
                            }
                          } @else {
                            <span class="no-requestor"
                              >No requestor provided</span
                            >
                          }
                        </div>
                      </div>
                    }
                  </td>
                </ng-container>

                <!-- Request Type Column -->
                <ng-container matColumnDef="requestType">
                  <td mat-cell *matCellDef="let request" class="type-cell">
                    @if (request.id < 0) {
                      <div class="skeleton-line medium"></div>
                    } @else {
                      {{
                        request.requestType?.name ||
                          request.requestType?.code ||
                          "Unknown"
                      }}
                    }
                  </td>
                </ng-container>

                <!-- Status Column -->
                <ng-container matColumnDef="status">
                  <td mat-cell *matCellDef="let request" class="status-cell">
                    @if (request.id < 0) {
                      <div class="skeleton-chip"></div>
                    } @else {
                      <span
                        class="badge"
                        [ngClass]="getStatusClass(request.status?.code)"
                      >
                        {{
                          request.status?.name ||
                            request.status?.code ||
                            "Unknown"
                        }}
                      </span>
                    }
                  </td>
                </ng-container>

                <!-- Assigned Column -->
                <ng-container matColumnDef="assigned">
                  <td mat-cell *matCellDef="let request" class="assigned-cell">
                    @if (request.id < 0) {
                      <div class="skeleton-line short"></div>
                    } @else if (request.assignedResourcesWithType?.length) {
                      <button
                        type="button"
                        class="assigned-count-link"
                        cdkOverlayOrigin
                        #assignedOrigin="cdkOverlayOrigin"
                        aria-label="Show assigned resources"
                        [attr.aria-expanded]="
                          assignedPopoverOrigin === assignedOrigin
                        "
                        (click)="
                          toggleAssignedPopover(request, assignedOrigin, $event)
                        "
                      >
                        {{ getAssignedValue(request.id) }}
                      </button>
                    } @else {
                      {{ getAssignedValue(request.id) }}
                    }
                  </td>
                </ng-container>

                <!-- Actions Column -->
                <ng-container matColumnDef="actions">
                  <td
                    mat-cell
                    *matCellDef="let request"
                    class="actions-cell"
                    (click)="$event.stopPropagation()"
                  >
                    @if (request.id >= 0) {
                      <div class="actions-cell-inner">
                        <button
                          mat-icon-button
                          class="action-btn"
                          matTooltip="View request details"
                          (click)="navigateToRequest(request, $event)"
                        >
                          <mat-icon>visibility</mat-icon>
                        </button>
                        <button
                          mat-icon-button
                          class="action-btn"
                          matTooltip="Open in floating window"
                          (click)="openInFloatingDialog(request, $event)"
                        >
                          <mat-icon>select_window_2</mat-icon>
                        </button>
                      </div>
                    }
                  </td>
                </ng-container>

                <tr
                  mat-row
                  *matRowDef="let _row; columns: displayedColumns"
                ></tr>
              </table>

              <!-- Assigned resources popover. A single overlay is reused by every row and
                   anchored to whichever Assigned cell was clicked. -->
              @if (assignedPopoverOrigin) {
                <ng-template
                  cdkConnectedOverlay
                  [cdkConnectedOverlayOrigin]="assignedPopoverOrigin"
                  [cdkConnectedOverlayOpen]="true"
                  [cdkConnectedOverlayPositions]="assignedPopoverPositions"
                  [cdkConnectedOverlayScrollStrategy]="
                    assignedPopoverScrollStrategy
                  "
                  [cdkConnectedOverlayPush]="true"
                  [cdkConnectedOverlayHasBackdrop]="true"
                  cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
                  (backdropClick)="closeAssignedPopover()"
                  (detach)="closeAssignedPopover()"
                >
                  <div
                    class="assigned-popover"
                    role="dialog"
                    aria-label="Assigned resources"
                  >
                    <div class="assigned-popover-label">Assigned Resources</div>
                    @for (group of assignedPopoverGroups; track $index) {
                      <div class="assigned-popover-group">
                        @if (assignedPopoverShowHeadings) {
                          <div class="assigned-popover-matter">
                            @if (group.clientMatterNumber) {
                              @if (group.matterName) {
                                <span class="assigned-popover-matter-name">{{
                                  group.matterName
                                }}</span>
                              }
                              <span class="assigned-popover-matter-number">{{
                                group.clientMatterNumber
                              }}</span>
                            } @else {
                              <span
                                class="assigned-popover-matter-name assigned-popover-matter-name--other"
                                >Other assignments</span
                              >
                            }
                          </div>
                        }
                        @for (resource of group.resources;
                          track resource.employeeId) {
                          <div class="assigned-popover-row">
                            @if (resource.canViewResource) {
                              <a
                                class="assigned-popover-name assigned-popover-name--link"
                                (click)="navigateToResource(resource.employeeId)"
                                >{{ assignedResourceName(resource) }}</a
                              >
                            } @else {
                              <span class="assigned-popover-name">{{
                                assignedResourceName(resource)
                              }}</span>
                            }
                            @let typeLabel = assignmentTypeLabel(resource);
                            @if (typeLabel) {
                              <span
                                class="membership-chip"
                                [class.membership-chip--primary]="
                                  resource.assignmentTypeCategory === 'PRIMARY'
                                "
                                [class.membership-chip--secondary]="
                                  resource.assignmentTypeCategory !== 'PRIMARY'
                                "
                                >{{ typeLabel }}</span
                              >
                            }
                          </div>
                        } @empty {
                          <div class="assigned-popover-empty">None assigned</div>
                        }
                      </div>
                    }
                  </div>
                </ng-template>
              }
            </div>

            <!-- Paginator -->
            @if (requests.length > 0) {
              <mat-paginator
                [length]="totalCount"
                [pageSize]="pageSize"
                [pageIndex]="pageIndex"
                [pageSizeOptions]="[10, 25, 50, 100]"
                (page)="onPageChange($event)"
                showFirstLastButtons
                class="requests-paginator"
              >
              </mat-paginator>
            }
          }
        </div>
      </mat-card>
    </div>
  </main>
</div>
